import React, { useState } from "react";
import { useLoginCode } from "../hooks/useLoginCode";
import { isDevEnvironment } from "env/runtimeEnv";
import { useVisualViewportLayout } from "hooks/useVisualViewportLayout";
import styles from "./LoginCodeInput.module.css";

interface LoginCodeInputProps {
  email: string;
  onCodeVerified: () => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
  isOnline: boolean;
  fullPage?: boolean;
}

export default function LoginCodeInput({
  email,
  onCodeVerified,
  onBack,
  isLoading,
  isOnline,
  fullPage = true,
}: LoginCodeInputProps) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const { verifyLoginCode } = useLoginCode();
  const { bottomInset } = useVisualViewportLayout();

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setCode(value);
      setError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code || code.length !== 6) {
      setError("Please enter the 6-digit code from your email");
      return;
    }

    if (!isOnline) {
      setError("You are offline. Please check your connection.");
      return;
    }

    setIsValidating(true);
    setError("");

    try {
      await verifyLoginCode(email, code);
      onCodeVerified();
    } catch (err: any) {
      setError(err.message || "Invalid code. Please try again.");
    } finally {
      setIsValidating(false);
    }
  };

  const containerClass = fullPage
    ? styles.container
    : `${styles.container} ${styles.inline}`;

  return (
    <form
      onSubmit={handleSubmit}
      className={containerClass}
      noValidate
    >
      <div className={styles.content}>
        {fullPage && (
          <div className={styles.header}>
            <div className={styles.logoContainer}>
              <div className={styles.logoWrapper}>
                <img
                  src="/logo512.png"
                  alt="Experience App Icon"
                  className={styles.logoImage}
                />
              </div>
            </div>

            {isDevEnvironment && (
              <h1 className={styles.envLabel}>DEV</h1>
            )}

            <h1 className={styles.title}>Your login code</h1>
          </div>
        )}

        <div className={styles.emailInfo}>
          <p className={styles.emailText}>
            We sent the code to <strong>{email}</strong>
          </p>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        <div className={styles.inputWrapper}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={code}
            onChange={handleCodeChange}
            placeholder="000000"
            maxLength={6}
            autoFocus
            className={styles.input}
            disabled={isValidating || isLoading || !isOnline}
          />
        </div>

        <div className={styles.resendContainer}>
          <button
            type="button"
            className={styles.resendButton}
            onClick={onBack}
          >
            Didn't receive the code? Resend email
          </button>
        </div>
      </div>

      <div
        className={styles.footer}
        style={{ "--keyboard-inset": `${bottomInset}px` } as React.CSSProperties}
      >
        <button
          type="submit"
          className={`proceed-button ${styles.submitButton}`}
          disabled={isValidating || isLoading || !isOnline || code.length !== 6}
        >
          {!isOnline
            ? "Offline - Check Connection"
            : isValidating || isLoading
              ? "Verifying..."
              : "Verify Code"}
        </button>
      </div>
    </form>
  );
}
